import { render as toPlainText } from 'datocms-structured-text-to-plain-text';
import { isCode, isHeading, isLink, isList, isListItem, isParagraph } from 'datocms-structured-text-utils';
import { StructuredText, renderMarkRule, renderNodeRule } from 'react-datocms';

import {
  IComponentCallToActionRecord,
  IComponentCardDeckRecord,
  IComponentHeadingRecord,
  IComponentPdfRecord,
  IComponentRichTableRecord,
  IComponentStatisticsPanelRecord,
  IComponentSwitchbackRecord,
  IFileField,
  Scalars,
} from 'graphql/types';

import color from 'atoms/colors/colors';

import Container from 'quarks/Container';
import Heading from 'quarks/Heading';
import Iframe from 'quarks/Iframe';
import Image from 'quarks/Image';
import Paragraph from 'quarks/Paragraph';
import Text from 'quarks/Text';

import Button, { ButtonProps } from 'molecules/Button/Button';

// import Link from 'molecules/Link';


import ComponentCardDeck from 'components/CardDeck/CardDeck';
import CodeSnippets from 'components/CodeSnippets/CodeSnippets';
import ComponentHeading from 'components/Heading/Heading';
import ComponentPdf from 'components/Pdf/Pdf';
import ComponentRichTable from 'components/RichTable/RichTable';
import StatisticsPanel from 'components/StatisticsPanel/StatisticsPanel';
import ComponentSwitchback from 'components/Switchback/Switchback';
import TableComponent from 'components/Table/Table';

import { getYoutubeId } from 'utils/functions';

export interface RichText {
  blocks: Array<Scalars['String']>;
  links: Array<IComponentCallToActionRecord | Scalars['String'] | Scalars['JsonField']>;
  value: Scalars['JsonField'];
}

const richTextParser = (content: RichText, isDark = false, isHero = false, ...blog: any) => {
  const customMarkRules = [
    renderMarkRule('strong', ({ children, key }) => (
      <Text fontWeight="bold" key={key}>
        {children}
      </Text>
    )),
    renderMarkRule('bold', ({ children, key }) => (
      <Text fontWeight="bold" key={key}>
        {children}
      </Text>
    )),
    renderMarkRule('italic', ({ children, key }) => (
      <Text fontStyle="italic" key={key}>
        {children}
      </Text>
    )),
    renderMarkRule('underline', ({ children, key }) => (
      <Text textDecoration="underline" key={key}>
        {children}
      </Text>
    )),
    renderMarkRule('code', ({ children, key }) => (
      <Text backgroundColor="cloud-200" fontFamily="codeFont" key={key}>
        {children}
      </Text>
    )),
  ];

  const customNodeRules = [
    renderNodeRule(isHeading, ({ node, children, key }) => {
      const HeadingTag = `h${node.level}`;
      type HeadingType = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const anchor = toPlainText(node)
        .toLowerCase()
        .replace(/ /g, '-')
        .replace(/[^\w-]+/g, '');

      return (
        <Heading as={HeadingTag as HeadingType} key={key} id={`${anchor}`}>
          {children}
        </Heading>
      );
    }),
    renderNodeRule(
      isParagraph,
      ({ children, key }) =>
        children && (
          <Paragraph key={key} textColor={isDark ? 'foam-50' : 'storm-900'}>
            {children}
          </Paragraph>
        ),
    ),
    renderNodeRule(isCode, ({ node, key }) => <CodeSnippets isDark={isDark} node={node} key={key} />),
    renderNodeRule(isLink, ({ children, node: { url }, key }) => (
      <a
        key={key}
        css={
          isDark
            ? `color: ${color.highSeas[400]};text-decoration: unset;`
            : `color: ${color.wave[850]};text-decoration: unset;`
        }
        href={url}
      >
        {children}
      </a>
    )),
    renderNodeRule(isList, ({ node, children, key }) => {
      const listStyle = node.style === 'bulleted' ? 'circle' : 'decimal';

      return (
        <Paragraph
          as={node.style === 'bulleted' ? 'ul' : 'ol'}
          key={key}
          marginBottom={40}
          fontSize="textXl"
          paddingLeft={32}
          css={{ listStyle }}
        >
          {children}
        </Paragraph>
      );
    }),
    renderNodeRule(isListItem, ({ children, key }) => (
      <Text as="li" key={key} marginBottom={12} fontSize="textLg">
        {children}
      </Text>
    )),
  ];

  const richTextElement = (
    <StructuredText
      data={content as any}
      customNodeRules={customNodeRules}
      customMarkRules={customMarkRules}
      renderInlineRecord={({ record }) => {
        switch (record.__typename) {
          case 'ComponentImageRecord':
            return (
              <Image
                src={(record?.imageDesktop as IFileField)?.url || ''}
                title={(record?.imageDesktop as IFileField)?.title}
                alt={(record?.imageDesktop as IFileField)?.alt || ''}
                maxWidth="100%"
              />
            );
          case 'ComponentVideoRecord':
            return (
              <Container position="relative" id="iframeContainer" css="padding-bottom: 56.5%;">
                <Iframe
                  src={`https://www.youtube.com/embed/${getYoutubeId((record?.videoUrl as IFileField).url)}`}
                  title={(record?.headline as string) || ''}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  allowFullScreen
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  scrolling="no"
                  loading="lazy"
                  position="absolute"
                  left={0}
                  top={0}
                />
              </Container>
            );
          case 'ComponentTableRecord':
            return (
              <Container>
                <TableComponent table={record.table} {...blog} />
              </Container>
            );
          case 'ComponentCardDeckRecord':
            return (
              <ComponentCardDeck
                {...(record as IComponentCardDeckRecord)}
                backgroundIsDark={isDark}
                key={record?.id}
                inContent={true}
              />
            );
          case 'ComponentStatisticsPanelRecord':
            return (
              <StatisticsPanel
                key={record?.id}
                {...(record as IComponentStatisticsPanelRecord)}
                backgroundIsDark={isDark}
                background={record?.background as string}
                inContent={true}
              />
            );
          case 'ComponentSwitchbackRecord':
            return (
              <Container>
                <ComponentSwitchback
                  {...(record as IComponentSwitchbackRecord)}
                  backgroundIsDark={isDark}
                  background={record?.background as string}
                  key={record?.id}
                  inContent={true}
                />
              </Container>
            );
          case 'ComponentHeadingRecord':
            return (
              <ComponentHeading
                {...(record as IComponentHeadingRecord)}
                backgroundIsDark={isDark}
                key={record?.id}
                inContent={true}
              />
            );
          case 'ComponentRichTableRecord':
            return (
              <ComponentRichTable
                {...(record as IComponentRichTableRecord)}
                key={record?.id}
                richTable={record?.richTable as string}
              />
            );
          case 'ComponentPdfRecord':
            return <ComponentPdf {...(record as IComponentPdfRecord)} key={record?.id} />;
          default:
            return (
              <Button
                key={record.id}
                href={record?.link as string}
                color={(record.variant as ButtonProps['color']) || 'primary'}
                size={(record.size as ButtonProps['size']) || 'md'}
                display="flex"
                width="100%"
                marginBottom={24}
                sm={{ display: 'inline-flex', width: 'auto', marginBottom: 0 }}
                startIcon={record.startIcon as any}
                endIcon={record.endIcon as any}
                className={(record?.classes as string) + ' ' + (isHero ? 'hero-button' : 'cta-button')}
              >
                {record.label as string}
              </Button>
            );
        }

        return <></>;
      }}
    />
  );

  return richTextElement;
};

export default richTextParser;
